import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Mail, Save, Loader2, Eye, EyeOff, Send, Image as ImageIcon, RotateCcw, Code2 } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = [
  {
    slug: 'verification',
    label: 'Email for Account Verification',
    hint: 'Sent to the customer right after they complete registration. Must contain {{verification_link}} so they can activate the account.',
    variables: [
      'first_name', 'email', 'verification_link', 'logo_block',
    ],
  },
  {
    slug: 'admin_notification',
    label: 'New Registration — Admin Alert',
    hint: 'Sent to admin (magictuners@gmail.com) on every new user registration. Fires automatically from POST /api/auth/register.',
    variables: [
      'first_name', 'surname', 'email', 'country', 'phone',
      'account_type', 'evc_block', 'company_name', 'vat',
      'address', 'zipcode', 'city',
      'newsletter_text', 'newsletter_badge', 'registered_at',
      'logo_block',
    ],
  },
  {
    slug: 'order_received',
    label: 'New Order — Customer copy',
    hint: 'Sent to the CUSTOMER right after they place an order. Does NOT contain the customer block (they know their own details). Admin gets a separate "New Order — Admin alert" template. Fires from POST /api/orders/.',
    variables: [
      'first_name', 'order_number', 'vehicle', 'vehicle_type',
      'manufacturer', 'model', 'engine', 'power', 'model_year',
      'gearbox', 'ecu_type', 'vin', 'file_type', 'reading_method',
      'tuning_options', 'priority', 'total_price', 'order_link', 'logo_block',
    ],
  },
  {
    slug: 'order_received_admin',
    label: 'New Order — Admin alert',
    hint: 'Sent to the admin (magictuners@gmail.com) right after a customer places an order. Includes the Customer block (full name, email, EVC license account, reseller name) so admins can identify the buyer at a glance. Fires alongside the customer copy from POST /api/orders/.',
    variables: [
      'order_number', 'customer_full_name', 'customer_email',
      'evc_customer_id', 'evc_reseller_name',
      'vehicle_type', 'manufacturer', 'model',
      'engine', 'power', 'model_year', 'gearbox', 'ecu_type',
      'vin', 'file_type', 'reading_method', 'tuning_options',
      'priority', 'subtotal', 'discount', 'total_price',
      'order_link', 'logo_block',
    ],
  },
  {
    slug: 'order_ready',
    label: 'Order Ready / File Available (sent to customer)',
    hint: 'Sent when the modified file is uploaded (or when the delayed-delivery timer flips the order to Completed). Contains the download CTA.',
    variables: [
      'first_name', 'order_number', 'vehicle', 'tuning_options',
      'order_link', 'logo_block',
    ],
  },
  {
    slug: 'order_cancelled',
    label: 'Order Cancelled (sent to customer)',
    hint: 'Sent when the order is cancelled by either admin or customer. Use {{cancellation_reason}} to render the reason text the admin typed.',
    variables: [
      'first_name', 'order_number', 'vehicle', 'total_price',
      'cancellation_reason', 'cancelled_by_text', 'logo_block',
    ],
  },
  {
    slug: 'order_cancelled_deleted',
    label: 'Order Cancelled and deleted (sent to customer)',
    hint: 'Sent when the order is cancelled AND permanently REMOVED from the system (different from a plain cancel which keeps the record). Reason banner and admin-typed reason text are rendered in RED so the customer sees it immediately.',
    variables: [
      'first_name', 'order_number', 'vehicle', 'total_price',
      'cancellation_reason', 'cancelled_by_text', 'logo_block',
    ],
  },
  {
    slug: 'original_file_request_admin',
    label: 'New Original File Request — Admin',
    hint: 'Sent to ADMIN_EMAIL when a customer places an Original File request. Use the per-column placeholders (hw_number, sw_number, calibration, etc) to lay it out exactly as you want.',
    variables: [
      'order_number', 'customer_name', 'customer_email', 'order_link',
      'brand_model', 'engine_type', 'hw_number', 'sw_number',
      'sw_upgrade', 'calibration', 'ecu', 'damos', 'driver_filename',
      'logo_block',
    ],
  },
  {
    slug: 'original_file_request_customer',
    label: 'New Original File Request — Customer',
    hint: 'Sent to the customer right after they create an Original File request. damos / driver_filename are intentionally blank in this email — operator-only fields.',
    variables: [
      'first_name', 'order_number', 'order_link', 'total_price',
      'brand_model', 'engine_type', 'hw_number', 'sw_number',
      'sw_upgrade', 'calibration', 'ecu', 'logo_block',
    ],
  },
  {
    slug: 'admin_payment_paypal',
    label: 'Payment received — PayPal (admin)',
    hint: 'Sent to the admin email when a customer recharges via PayPal. The subject is auto-generated as "PayPal payment {{amount}} € from {{customer_name}}". {{whatsapp_block}} renders a WhatsApp deep-link button only if the customer has a phone on file.',
    variables: [
      'amount', 'customer_account_id', 'customer_name', 'customer_email',
      'txn_id', 'company_short', 'customer_balance', 'whatsapp_block', 'logo_block',
    ],
  },
  {
    slug: 'admin_payment_stripe',
    label: 'Payment received — Stripe (admin)',
    hint: 'Sent to the admin email when a customer recharges via Stripe. company_short for Stripe is always UK (per business rule). txn_id is the Stripe payment_intent id (pi_xxx).',
    variables: [
      'amount', 'customer_account_id', 'customer_name', 'customer_email',
      'txn_id', 'company_short', 'customer_balance', 'whatsapp_block', 'logo_block',
    ],
  },
  {
    slug: 'recharge_invoice',
    label: 'Recharge Invoice (sent to customer)',
    hint: 'Sent after a successful top-up. A PDF invoice is always attached automatically.',
    variables: [
      'first_name', 'invoice_number', 'transaction_date',
      'amount', 'bonus_line', 'new_balance', 'company_name', 'logo_block',
    ],
  },
  {
    slug: 'ticket_message',
    label: 'New Ticket Message (sent to counterparty)',
    hint: 'Sent each time a new chat message is posted in an order ticket. Admin gets it when the client writes; client gets it when admin (or bot) writes. Attachments are never embedded — the full {{message_html}} body is included so the recipient can read without leaving their inbox.',
    variables: [
      'first_name', 'order_number', 'vehicle', 'status',
      'sender_name', 'sent_at', 'message_html', 'order_link', 'logo_block',
    ],
  },
  {
    slug: 'password_reset',
    label: 'Password Reset (sent to customer)',
    hint: 'Sent when a customer requests "Forgot password" on the login screen. The {{reset_link}} variable is mandatory — it embeds the one-time token used to identify the user. Link expires in 1 hour.',
    variables: [
      'first_name', 'email', 'reset_link', 'logo_block',
    ],
  },
  {
    slug: 'welcome_bonus',
    label: 'Welcome Bonus (sent to customer)',
    hint: 'Sent once, right after the customer verifies their email at registration — BEFORE they make any deposit. The email ANNOUNCES the reserved bonus and explains how to claim it (top up ≥ {{min_deposit}} within {{validity_days}} days). It is NOT a confirmation that the bonus was credited — that happens later, when the first deposit lands. Fires only for fresh signups (never for migrated legacy users). Amount, min deposit and validity are read from Settings → Welcome bonus tab.',
    variables: [
      'first_name', 'bonus_amount', 'min_deposit', 'validity_days',
      'dashboard_link', 'logo_block',
    ],
  },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image', 'clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'align', 'link', 'image',
];

// Dummy values plugged into every {{placeholder}} on the live preview
// pane so the admin sees what the email will look like with real data
// without having to send a test send to themselves. Centralised here so
// adding a new placeholder = single-line addition.
const SAMPLE_VARS = {
  // common
  first_name: 'Dzmitry',
  surname: 'Barysevich',
  full_name: 'Dzmitry Barysevich',
  email: 'demo@example.com',
  user_email: 'demo@example.com',
  customer_email: 'demo@example.com',
  customer_name: 'Dzmitry Barysevich',
  customer_account_id: '#12345',
  country: 'Lithuania',
  phone: '+370 600 12345',
  registered_at: '11.05.2026 14:16',
  account_type: 'Individual',
  company_name: 'Demo Garage Ltd.',
  vat: 'LT1234567890',
  address: 'Vilniaus g. 5',
  zipcode: '01100',
  city: 'Vilnius',
  newsletter_text: 'Subscribed to marketing newsletter',
  newsletter_badge: 'YES',
  evc_block: '',
  // order
  order_number: '11052026-1',
  vehicle: 'BMW X2 2000 192HP',
  brand_model: 'BMW X2',
  engine_type: '2.0 TDI 192HP',
  ecu: 'Bosch EDC17C41',
  hw_number: '0281019912',
  sw_number: '1037539832',
  sw_upgrade: '—',
  calibration: '8R2200',
  damos: 'EDC17C41_R1',
  driver_filename: 'EDC17C41_F32_BMW.kp3',
  tuning_options: 'Stage 1 · Gearbox · EGR Off',
  priority: 'High',
  total_price: '€180.00',
  cancellation_reason: 'Customer requested cancellation',
  cancelled_by: 'Admin',
  cancelled_by_text: 'Admin',
  order_link: 'https://auto-remap.com/orders/11052026-1',
  verification_link: 'https://auto-remap.com/verify?token=abc123def456',
  reset_link: 'https://auto-remap.com/reset-password?token=def456abc789',
  dashboard_link: 'https://auto-remap.com/dashboard',
  // payments
  amount: '€500.00',
  bonus_amount: '50',
  bonus_line: '+ €50 bonus credited',
  new_balance: '€920.00',
  min_deposit: '40',
  validity_days: '30',
  txn_id: '6GSO5151707408',
  paypal_txn: '6GSO5151707408',
  stripe_txn: 'pi_3PqQqL2eZvKYlo2C0a1b2c3d',
  issuer: 'USA',
  company_short: 'UK',
  customer_balance: '€420.00',
  whatsapp_block: '',
  invoice_number: 'INV-2026-0042',
  transaction_date: '11.05.2026 14:16',
  // tickets
  status: 'In progress',
  sender_name: 'Admin (Support)',
  sent_at: '11.05.2026 14:25',
  message_preview: 'Hi! Your file is ready, please test on a stationary engine first.',
  message_html: '<p>Hi! Your file is ready, please test on a stationary engine first.</p>',
  // logo/signature/block placeholders are rendered server-side at send;
  // for the preview we leave them empty so the unified shell's bundled
  // header logo carries the brand.
  logo_block: '',
  signature_block: '',
};

/** Substitute {{var}} occurrences inside `html` using SAMPLE_VARS. */
const renderSamplePreview = (html) => {
  if (!html) return '';
  return html.replace(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g, (_, name) =>
    Object.prototype.hasOwnProperty.call(SAMPLE_VARS, name) ? SAMPLE_VARS[name] : `{{${name}}}`,
  );
};

/**
 * Admin Email Templates editor. Each template owns its own subject + HTML
 * body + logo URL. Saves to MongoDB via /api/config/email-templates/:slug.
 */
export default function EmailTemplatesEditor({ token }) {
  const [active, setActive] = useState('verification');
  const [tpl, setTpl] = useState({});           // {slug: {subject, html, logo_url, default_*}}
  const [drafts, setDrafts] = useState({});     // {slug: {subject, html, logo_url}}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  // Code mode swaps the Quill WYSIWYG editor for a plain monospace
  // textarea so the admin can tweak inline CSS / hand-craft HTML
  // without Quill rewriting it. Defaults to ON because our bundled
  // email templates use table-based / inline-styled HTML that Quill
  // strips as soon as its onChange fires — flipping to Rich text is
  // a deliberate action.
  const [codeMode, setCodeMode] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testStatus, setTestStatus] = useState({ type: '', text: '' });

  // Logo upload state
  // `uploading` carries the action label ('template' or 'default') so the
  // two action buttons can show their own busy state independently.
  const [uploading, setUploading] = useState(null);
  const [defaultLogoUrl, setDefaultLogoUrl] = useState('');
  const fileInputRef = useRef(null);
  // 'template' = paste into this template's logo_url override
  // 'default'  = set as brand-wide default for ALL templates
  const [pendingUploadTarget, setPendingUploadTarget] = useState('template');

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/config/email-templates`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const list = await r.json();
      const map = {};
      const dr = {};
      list.forEach((t) => {
        map[t.slug] = t;
        // Pre-fill drafts: use override if present, otherwise the default
        // so the admin can immediately edit a real template.
        dr[t.slug] = {
          subject:  t.subject  || t.default_subject || '',
          html:     t.html     || t.default_html    || '',
          logo_url: t.logo_url || '',
        };
      });
      setTpl(map);
      setDrafts(dr);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Load the brand-wide default logo URL — separate from per-template
  // overrides. Used by the "Set as default" preview and the "Clear"
  // button.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/branding/default-logo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const j = await r.json();
        setDefaultLogoUrl(j.url || '');
      } catch { /* non-fatal */ }
    })();
  }, [token]);

  /** Pick a file and upload it. `target` decides what happens with the
   *  resulting URL:
   *    'template' → put it into this template's logo_url override
   *    'default'  → store as the brand-wide default for ALL templates
   */
  const triggerUpload = (target) => {
    setPendingUploadTarget(target);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const target = pendingUploadTarget;
    setUploading(target);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/config/branding/upload-logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert('Upload failed: ' + (j.detail || `HTTP ${r.status}`));
        return;
      }
      // The backend returns a relative URL like /api/config/branding-asset/foo.png
      // We store/display it relative so the same URL works from any frontend
      // origin; the backend resolves it to absolute when building the email.
      const url = j.url;
      if (target === 'default') {
        const r2 = await fetch(`${API_URL}/api/config/branding/default-logo`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ url }),
        });
        if (!r2.ok) {
          const j2 = await r2.json().catch(() => ({}));
          alert('Failed to save as default: ' + (j2.detail || `HTTP ${r2.status}`));
          return;
        }
        setDefaultLogoUrl(url);
        alert('Logo uploaded and set as brand-wide default for ALL email templates.');
      } else {
        // Template-only: put the URL into the current draft. Admin must
        // still click "Save template" to persist — that mirrors how
        // every other field works and avoids surprise saves on upload.
        updateDraft('logo_url', url);
        alert('Logo uploaded. Click "Save template" below to apply it to this template only.');
      }
    } finally {
      setUploading(null);
    }
  };

  const clearDefaultLogo = async () => {
    if (!confirm('Clear brand-wide default logo? Templates without their own override will fall back to the built-in AUTO-REMAP logo.')) return;
    setUploading('default');
    try {
      const r = await fetch(`${API_URL}/api/config/branding/default-logo`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: '' }),
      });
      if (r.ok) setDefaultLogoUrl('');
    } finally {
      setUploading(null);
    }
  };

  const updateDraft = (field, value) => {
    setDrafts((d) => ({ ...d, [active]: { ...(d[active] || {}), [field]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/config/email-templates/${active}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(drafts[active] || {}),
      });
      if (!r.ok) { alert('Save failed: ' + await r.text()); return; }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      load();
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!window.confirm('Reset this template to the built-in default? Your custom changes will be lost.')) return;
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/config/email-templates/${active}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ subject: '', html: '', logo_url: '' }),
      });
      load();
    } finally { setSaving(false); }
  };

  // Wipe any admin edits and reload the bundled styled version of this
  // template from the server. Use when a deploy ships new template
  // styling but the DB copy was previously admin-edited (the startup
  // autosync respects admin edits and leaves it untouched, so the
  // admin needs to opt-in to refreshing it).
  const restoreFromBundle = async () => {
    if (!window.confirm(
      'Restore this template from the latest bundled file?\n\n' +
      'This wipes ALL admin edits to the HTML body and replaces them ' +
      'with the freshly-styled version from the codebase. The subject ' +
      'line is preserved.\n\nProceed?'
    )) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/config/email-templates/${active}/restore-from-bundle`, {
        method: 'POST', headers,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert('Restore failed: ' + (j.detail || `HTTP ${r.status}`));
        return;
      }
      alert(`Restored from ${j.filename} (${j.html_size} bytes).`);
      load();
    } finally { setSaving(false); }
  };

  // Bulk-restore: wipe admin edits for every template that has a
  // bundle file and reload the styled versions in one click. Used
  // after a deploy or when several templates got mangled (e.g. by a
  // WYSIWYG editor stripping table-based email HTML).
  const restoreAllFromBundle = async () => {
    if (!window.confirm(
      'Restore ALL templates from the latest bundled files?\n\n' +
      'This wipes admin edits to the HTML body of EVERY template ' +
      'that has a bundled version. Subject lines are preserved.\n\n' +
      'Proceed?'
    )) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/config/email-templates/restore-all-from-bundle`, {
        method: 'POST', headers,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert('Bulk restore failed: ' + (j.detail || `HTTP ${r.status}`));
        return;
      }
      alert(
        `Restored ${j.restored} / ${j.total} templates from bundle.\n` +
        (j.skipped_no_bundle_file ? `Skipped (no bundle file): ${j.skipped_no_bundle_file.join(', ')}\n` : '') +
        (j.errors && j.errors.length ? `Errors: ${j.errors.join(', ')}` : '')
      );
      load();
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      setTestStatus({ type: 'error', text: 'Enter a valid email first.' });
      return;
    }
    setSendingTest(true);
    setTestStatus({ type: '', text: '' });
    try {
      const r = await fetch(`${API_URL}/api/config/email-templates/${active}/test`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ to: testEmail, ...drafts[active] }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setTestStatus({ type: 'error', text: body.detail || `HTTP ${r.status}` });
      } else if (body.sent) {
        setTestStatus({ type: 'success', text: `Sent to ${active === 'admin_notification' ? 'admin email (magictuners@gmail.com)' : testEmail}.` });
      } else {
        setTestStatus({ type: 'error', text: 'Send returned false. Check SMTP config.' });
      }
    } catch (e) {
      setTestStatus({ type: 'error', text: e.message || 'Network error' });
    } finally { setSendingTest(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center text-slate-600">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading email templates…
      </div>
    );
  }

  const meta = TEMPLATES.find((t) => t.slug === active);
  const draft = drafts[active] || { subject: '', html: '', logo_url: '' };
  const cur = tpl[active] || {};

  return (
    <div className="space-y-5" data-testid="email-templates-editor">
      <div className="flex items-start gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-xl">
        <Mail className="w-5 h-5 text-cyan-700 mt-0.5" />
        <div className="text-sm text-cyan-900 flex-1">
          <p className="font-semibold mb-1">Email Templates</p>
          <p className="text-cyan-800">
            Customize the emails sent to customers and to you (admin). Use <code className="bg-white px-1 rounded">{'{{variable}}'}</code> placeholders to inject data —
            available variables are listed under each template.
          </p>
        </div>
        <button
          type="button"
          onClick={restoreAllFromBundle}
          disabled={saving}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold rounded-lg shadow-sm disabled:opacity-50"
          title="Bulk-restore every template that has a bundle file to the freshly-styled version. Subject lines are preserved."
          data-testid="restore-all-bundle-email-templates"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Restore ALL from bundle
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TEMPLATES.map((t) => (
          <button
            key={t.slug}
            type="button"
            onClick={() => { setActive(t.slug); setPreviewing(false); }}
            data-testid={`email-tab-${t.slug}`}
            className={`px-4 py-2 text-sm font-medium relative ${
              active === t.slug ? 'text-cyan-700' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <span className="flex items-center gap-2">
              {t.label}
              {tpl[t.slug]?.has_override
                ? <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">customized</span>
                : <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full">default</span>}
            </span>
            {active === t.slug && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-600" />
            )}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-base font-bold text-slate-800">{meta?.label}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{meta?.hint}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                // Warn before switching to Rich text if the HTML uses
                // table-based email markup — Quill will strip it as
                // soon as it renders, which historically destroyed
                // bundled templates. Code mode → Rich text is a
                // one-way street unless the admin restores from
                // bundle again.
                if (codeMode && /<table[\s>]/i.test(draft.html || '')) {
                  if (!window.confirm(
                    'This template uses table-based email HTML. ' +
                    'Rich text mode will strip tables, inline styles ' +
                    'and layout — the message will look plain. Only ' +
                    'switch if you plan to rebuild the layout from ' +
                    'scratch.\n\nSwitch anyway?'
                  )) return;
                }
                setCodeMode((c) => !c);
              }}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium ${
                codeMode
                  ? 'bg-slate-800 text-white hover:bg-slate-900'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
              data-testid="email-toggle-codemode"
              title={codeMode ? 'Switch to rich-text editor (WARNING: destroys table-based HTML)' : 'Edit raw HTML / inline CSS'}
            >
              <Code2 className="w-3.5 h-3.5" />
              {codeMode ? 'Rich text' : 'Code mode'}
            </button>
            <button
              type="button"
              onClick={() => setPreviewing((p) => !p)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"
              data-testid="email-toggle-preview"
              title="Full-width preview"
            >
              {previewing ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {previewing ? 'Back to editor' : 'Full preview'}
            </button>
          </div>
        </div>

        {/* Variable reference */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
          <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1.5">Available variables</p>
          <div className="flex flex-wrap gap-1">
            {(meta?.variables || []).map((v) => (
              <code key={v} className="text-[11px] bg-white border border-slate-200 px-1.5 py-0.5 rounded">
                {`{{${v}}}`}
              </code>
            ))}
          </div>
        </div>

        {/* Subject */}
        <div className="mb-4">
          <label className="block text-xs font-bold text-slate-700 mb-1">Email subject</label>
          <input
            type="text"
            value={draft.subject}
            onChange={(e) => updateDraft('subject', e.target.value)}
            data-testid="email-subject-input"
            placeholder={cur.default_subject || 'Subject line shown in the inbox'}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        {/* Logo URL + uploaders */}
        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <label className="block text-xs font-bold text-slate-700 mb-2 flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5" /> Email logo
          </label>

          {/* Hidden input — clicked programmatically by the two action buttons */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,image/gif"
            onChange={handleFileSelected}
            style={{ display: 'none' }}
            data-testid="email-logo-file-input"
          />

          {/* Two action buttons — clear visual separation between
              "this template only" and "every email" so the admin can't
              confuse the two. The default-target also shows what is
              currently the brand-wide default. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            <button
              type="button"
              onClick={() => triggerUpload('template')}
              disabled={!!uploading}
              data-testid="upload-logo-template-btn"
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-white border border-cyan-300 hover:bg-cyan-50 text-cyan-700 rounded-lg text-xs font-bold transition disabled:opacity-50"
            >
              {uploading === 'template' ? (
                <span className="w-3 h-3 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              ) : <ImageIcon className="w-3.5 h-3.5" />}
              Upload &amp; use in <span className="underline">this template only</span>
            </button>
            <button
              type="button"
              onClick={() => triggerUpload('default')}
              disabled={!!uploading}
              data-testid="upload-logo-default-btn"
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-xs font-bold transition disabled:opacity-50"
            >
              {uploading === 'default' ? (
                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : <ImageIcon className="w-3.5 h-3.5" />}
              Upload &amp; set as <span className="underline">default for ALL templates</span>
            </button>
          </div>

          {/* Brand-wide default preview */}
          {defaultLogoUrl && (
            <div className="mb-3 p-2 bg-cyan-50 border border-cyan-200 rounded flex items-center gap-3" data-testid="default-logo-row">
              <div className="text-[11px] font-bold text-cyan-800 whitespace-nowrap">Current default:</div>
              <img
                src={defaultLogoUrl.startsWith('http') ? defaultLogoUrl : `${API_URL}${defaultLogoUrl}`}
                alt="Brand default"
                className="max-h-10 bg-white px-2 py-1 rounded"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="flex-1 text-[10px] font-mono text-cyan-700 truncate" title={defaultLogoUrl}>{defaultLogoUrl}</div>
              <button
                type="button"
                onClick={clearDefaultLogo}
                disabled={uploading === 'default'}
                data-testid="clear-default-logo-btn"
                className="text-[10px] font-bold text-red-600 hover:text-red-700 hover:underline disabled:opacity-50"
              >Clear</button>
            </div>
          )}

          {/* Manual URL input — kept for advanced use (paste from external CDN) */}
          <label className="block text-[11px] font-bold text-slate-500 mb-1 uppercase tracking-wider">Or paste a URL manually</label>
          <input
            type="url"
            value={draft.logo_url}
            onChange={(e) => updateDraft('logo_url', e.target.value)}
            data-testid="email-logo-url-input"
            placeholder="https://your-cdn.com/logo.png  or  /api/config/branding-asset/foo.png"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            This URL renders inside <code className="bg-slate-100 px-1 rounded">{'{{logo_block}}'}</code> for <b>this template only</b>.
            Leave empty to fall back to the brand-wide default above (or the built-in logo if no default is set).
          </p>
          {draft.logo_url && (
            <div className="mt-2 p-2 bg-slate-100 rounded inline-block">
              <img
                src={draft.logo_url.startsWith('http') ? draft.logo_url : `${API_URL}${draft.logo_url}`}
                alt="Logo preview"
                className="max-h-12"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
        </div>

        {/* HTML body — editor on the left, live preview on the right.
            On wide screens both panes are visible side-by-side so the
            admin sees the rendered email update as they type. The "Full
            preview" toggle still hides the editor for a roomy view.
            Mobile/narrow screens collapse to a single column with the
            preview below the editor. */}
        <label className="block text-xs font-bold text-slate-700 mb-1">Email body (HTML / rich text)</label>
        {previewing ? (
          <div
            className="border border-slate-200 rounded-lg bg-white overflow-hidden"
            data-testid="email-preview"
          >
            <iframe
              title="Email preview"
              srcDoc={renderSamplePreview(draft.html)}
              sandbox=""
              className="w-full"
              style={{ minHeight: 720, border: 0 }}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div
              className={
                codeMode
                  ? 'border border-slate-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-cyan-500 bg-slate-950'
                  : 'quill-wrapper border border-slate-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-cyan-500'
              }
              data-testid={codeMode ? 'email-code-editor' : 'email-quill-editor'}
            >
              {codeMode ? (
                <textarea
                  value={draft.html}
                  onChange={(e) => updateDraft('html', e.target.value)}
                  spellCheck={false}
                  className="w-full bg-slate-950 text-slate-100 p-4 font-mono text-[12px] leading-relaxed outline-none resize-none"
                  style={{ minHeight: 560 }}
                  data-testid="email-code-textarea"
                  placeholder="<!doctype html><html>… raw HTML with {{placeholders}} …</html>"
                />
              ) : (
                <ReactQuill
                  key={active}
                  theme="snow"
                  value={draft.html}
                  onChange={(html) => updateDraft('html', html)}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="HTML email body. You can paste raw HTML — the live preview to the right updates as you type."
                />
              )}
            </div>
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Live preview</span>
                <span className="text-[10px] text-slate-400">{'{{placeholders}}'} are filled with sample data</span>
              </div>
              <div
                className="border border-slate-200 rounded-lg bg-slate-50 overflow-hidden flex-1"
                data-testid="email-live-preview"
              >
                <iframe
                  title="Email live preview"
                  srcDoc={renderSamplePreview(draft.html)}
                  sandbox=""
                  className="w-full"
                  style={{ minHeight: 560, border: 0, background: '#f0f2f5' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Action row */}
        <div className="flex flex-wrap gap-2 items-center mt-4">
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-email-template"
            className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium disabled:opacity-50 hover:shadow-md flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save template
          </button>
          <button
            onClick={resetToDefault}
            disabled={saving}
            className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-2"
            data-testid="reset-email-template"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset to default
          </button>
          <button
            onClick={restoreFromBundle}
            disabled={saving}
            className="px-3 py-2 bg-violet-50 border border-violet-300 rounded-lg text-sm text-violet-800 hover:bg-violet-100 flex items-center gap-2"
            title="Wipe admin edits and reload the latest styled version from the bundled file (e.g. after a deploy that updated the template)."
            data-testid="restore-bundle-email-template"
          >
            <Code2 className="w-3.5 h-3.5" /> Restore from bundle
          </button>
          {savedFlash && <span className="text-emerald-600 text-sm font-medium">✓ Saved</span>}
        </div>

        {/* Test send */}
        <div className="mt-5 pt-5 border-t border-slate-100">
          <label className="block text-xs font-bold text-slate-700 mb-1.5">Send test email</label>
          <div className="flex gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="email-test-recipient"
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <button
              onClick={sendTest}
              disabled={sendingTest}
              data-testid="email-send-test"
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg text-sm font-medium hover:shadow flex items-center gap-2 disabled:opacity-50"
            >
              {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
            </button>
          </div>
          {testStatus.text && (
            <p className={`text-xs mt-2 ${testStatus.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`} data-testid="email-test-status">
              {testStatus.text}
            </p>
          )}
          {active === 'admin_notification' && (
            <p className="text-[11px] text-slate-400 mt-1">
              Note: admin notifications always go to <code>magictuners@gmail.com</code>. Use the test field to verify it works.
            </p>
          )}
          {(active === 'order_received' || active === 'order_ready' || active === 'order_cancelled' || active === 'original_file_request_admin' || active === 'original_file_request_customer' || active === 'admin_payment_paypal' || active === 'admin_payment_stripe') && (
            <p className="text-[11px] text-slate-400 mt-1">
              Note: the test email uses a sample order (<code>ORD-PREVIEW-0001</code> · VW Golf 2.0 TDI) so you can preview the layout end-to-end.
            </p>
          )}
        </div>
      </div>

      <style>{`
        .quill-wrapper .ql-toolbar { border: 0; border-bottom: 1px solid #e2e8f0; background: #f8fafc; }
        .quill-wrapper .ql-container { border: 0; min-height: 360px; font-size: 13px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; }
        .quill-wrapper .ql-editor { min-height: 360px; }
      `}</style>
    </div>
  );
}
